/* One id costs 39 bytes once comma-encoded, so 80 keeps a request near 3KB —
   inside the 4KB the edge accepts, with room for the path and the rest of the
   query. Measured against prod, not guessed. */
const HYDRATE_CHUNK = 80;
const UUID_RE = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;

export default {
  // Nodes
  async getNodes(params = {}) {
    const query = new URLSearchParams(params).toString();
    const response = await this.request(`/nodes${query ? '?' + query : ''}`);
    return response.nodes || [];
  },

  async getNode(nodeId) {
    return this.request(`/nodes/${nodeId}`);
  },

  async createNode(title, valueJson = {}, schemaId = null, groupId = null) {
    const body = { title, value_json: valueJson };
    if (schemaId) body.schema_id = schemaId;
    if (groupId) body.group_id = groupId;
    return this.request('/nodes', { method: 'POST', body });
  },

  async updateNode(nodeId, updates) {
    return this.request(`/nodes/${nodeId}`, {
      method: 'PUT',
      body: updates,
    });
  },

  // PUT merges, so it can set a field but never CLEAR one. Clearing is an RFC
  // 6902 op of its own: patchNode(id, [{ op: 'remove', path: '/value_json/x' }]).
  // Removing a key that isn't there is an error per the RFC, so read first.
  async patchNode(nodeId, ops) {
    return this.request(`/nodes/${nodeId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json-patch+json' },
      body: ops,
    });
  },

  async deleteNode(nodeId) {
    return this.request(`/nodes/${nodeId}`, {
      method: 'DELETE',
    });
  },

  async bulkDeleteNodes(nodeIds) {
    return this.request('/nodes/bulk-delete', {
      method: 'POST',
      body: { node_ids: nodeIds },
    });
  },

  async deleteAllNodes() {
    return this.request('/nodes/delete-all', {
      method: 'POST',
      body: { confirm: true },
    });
  },

  async getTrashedNodes(params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.request(`/nodes/trash${query ? '?' + query : ''}`);
  },

  // Trash -> active. Replays the relationships the node had when it was trashed;
  // the response reports edges_restored and any edges_skipped (an edge whose other
  // endpoint has since been deleted is not resurrected).
  async restoreNode(nodeId, { force = false } = {}) {
    return this.request(`/nodes/${nodeId}/restore${force ? '?force=true' : ''}`, {
      method: 'POST',
    });
  },

  // Irreversible. The node must already be in the trash — the server returns 409
  // NOT_TRASHED otherwise, so nothing is destroyed by a single call.
  async purgeNode(nodeId) {
    return this.request(`/nodes/${nodeId}/purge`, { method: 'DELETE' });
  },

  // Up to 200 trashed nodes in one request: { purged: [ids], failed: [{id, code, message}] }
  async purgeTrashed(nodeIds) {
    return this.request('/nodes/trash/purge', { method: 'POST', body: { node_ids: nodeIds } });
  },

  // File Upload (files are now nodes with rawfile schema)
  async uploadFileAsNode(file, metadata = {}) {
    return this.uploadFileTo('/nodes', file, metadata);
  },

  // The upload, into a nodes collection: '/nodes' for your own files, or
  // '/groups/<id>/nodes' for a file the group owns. Both expose the same
  // upload/sas + upload pair.
  async uploadFileTo(nodesPath, file, metadata = {}) {
    // Step 1: get a short-lived SAS write URL from the server
    const sasResp = await this.request(
      `${nodesPath}/upload/sas?filename=${encodeURIComponent(file.name)}&content_type=${encodeURIComponent(file.type || 'application/octet-stream')}`
    );
    const { upload_url, blob_key } = sasResp;

    // Step 2: PUT the bytes straight to object storage (no Flask involved).
    //
    // Content-Type ONLY. S3 signs it into the presigned URL, so it has to match
    // what step 1 asked for. The x-ms-blob-type: BlockBlob that sat here is
    // Azure's, and it outlived the move to S3 — inert, because S3 ignores
    // unsigned headers, which is exactly why nobody noticed. Every extra header
    // is also one more thing the bucket's CORS preflight has to permit.
    const putResp = await fetch(upload_url, {
      method: 'PUT',
      headers: { 'Content-Type': file.type || 'application/octet-stream' },
      body: file,
    });
    if (!putResp.ok) {
      throw new Error(`Blob upload failed: ${putResp.status}`);
    }

    // Step 3: compute checksum and create the node record
    const buffer = await file.arrayBuffer();
    const hashBuffer = await crypto.subtle.digest('SHA-256', buffer);
    const checksum = Array.from(new Uint8Array(hashBuffer))
      .map(b => b.toString(16).padStart(2, '0')).join('');

    return this.request(`${nodesPath}/upload`, {
      method: 'POST',
      body: JSON.stringify({
        blob_key,
        filename: file.name,
        mime_type: file.type || 'application/octet-stream',
        size_bytes: file.size,
        checksum_sha256: checksum,
        metadata,
      }),
    });
  },

  async getNodeDownloadUrl(nodeId) {
    return this.request(`/nodes/${nodeId}/download`);
  },

  // ── Relationships ────────────────────────────────────────────────────────
  //
  // There is no /relationships resource. A relationship IS a node, on the
  // `relationship` schema, reached through /nodes like everything else.
  //
  // These helpers keep their names and their return shapes so no component
  // changes: callers still get { from_node_id, to_node_id, relationship_type },
  // and still get hydrated `from_node` / `to_node` when they ask for them.

  RELATIONSHIP_SCHEMA: 'relationship',
  BELONGS_TO_SCHEMA: 'belongs_to',

  // A relationship NODE in the shape callers have always received.
  // `belongs_to` has no relationship_type property — the schema IS the type — so
  // the name is recovered from the schema, or every belongs_to link comes back
  // typeless.
  _asRelationship(node) {
    const vj = (node && node.value_json) || {};
    const first = (k) => (Array.isArray(vj[k]) ? vj[k][0] : vj[k]) ?? null;
    let relType = vj.relationship_type || null;
    if (!relType && node.schema_name === 'belongs_to') relType = 'belongs_to';
    return {
      id: node.id,
      owner_id: node.owner_id,
      from_node_id: first('from'),
      to_node_id: first('to'),
      relationship_type: relType,
      context: vj.context ?? null,
      bidirectional: !!vj.bidirectional,
      metadata: vj.metadata ?? null,
      created_at: node.created_at,
      updated_at: node.updated_at,
    };
  },

  // Attach from_node / to_node. A FEW requests for every endpoint across the whole
  // batch, not one per relationship — a graph view has hundreds of edges and an
  // N+1 here is the difference between a page load and a stall.
  //
  // Chunked, because it used to be exactly ONE request and the edge rejects a URL
  // over 4KB: 100 ids answered 200, 105 answered 400, and past ~250 it became a
  // 414. It never reached the application — there is no log line for it — so it
  // read as a backend bug until somebody measured it.
  //
  // It only started failing when relationships became nodes: the same page now
  // carries far more edges, so the id list crossed 4KB.
  async _hydrateEnds(rels, basePath = '/nodes') {
    const ids = [...new Set(rels.flatMap((r) => [r.from_node_id, r.to_node_id]).filter(Boolean))]
      // A ref that is not a node id can never resolve, and asking for it spends
      // URL budget to be told nothing. Prod carries these: relationship nodes
      // written with a client-side placeholder (`tmp_...`) still hold it.
      .filter((id) => UUID_RE.test(id));
    if (!ids.length) return rels;

    const chunks = [];
    for (let i = 0; i < ids.length; i += HYDRATE_CHUNK) chunks.push(ids.slice(i, i + HYDRATE_CHUNK));
    const pages = await Promise.all(chunks.map((chunk) =>
      this.request(`${basePath}?ids=${chunk.join(',')}&limit=${chunk.length}`)));
    const byId = Object.fromEntries(pages.flatMap((p) => (p.nodes || []).map((n) => [n.id, n])));
    return rels.map((r) => ({
      ...r,
      // null, not undefined, when an endpoint is not readable — the caller can
      // tell "withheld" from "not asked for".
      from_node: byId[r.from_node_id] ?? null,
      to_node: byId[r.to_node_id] ?? null,
    }));
  },

  async _listRelationshipNodes(basePath, params = {}) {
    const { from_node_id, to_node_id, relationship_type, include_nodes, ...rest } = params;
    const q = new URLSearchParams({ limit: '500', ...rest });
    q.set('schema_name', relationship_type === 'belongs_to' ? 'belongs_to' : 'relationship');
    if (from_node_id) q.set('ref[from]', from_node_id);
    if (to_node_id) q.set('ref[to]', to_node_id);
    const resp = await this.request(`${basePath}?${q.toString()}`);
    let rels = (resp.nodes || []).map((n) => this._asRelationship(n));
    if (relationship_type && relationship_type !== 'belongs_to') {
      rels = rels.filter((r) => r.relationship_type === relationship_type);
    }
    // Default true, matching the old endpoint — six components read from_node/to_node.
    if (include_nodes === undefined || include_nodes === true || include_nodes === 'true') {
      rels = await this._hydrateEnds(rels, basePath);
    }
    return rels;
  },

  async getRelationships(params = {}) {
    return this._listRelationshipNodes('/nodes', params);
  },

  async getGroupRelationships(groupId, params = {}) {
    return this._listRelationshipNodes(`/groups/${groupId}/nodes`, params);
  },

  // Every link touching this node, either end. Two queries, because direction is
  // meaningful and the server offers no either-direction filter that would
  // conflate "A employs B" with "B employs A". Neither needs the other's answer,
  // so they go together: the inspector waits one round trip, not two.
  // Both link schemas: a real belongs_to is a link too, and asking for
  // `relationship` alone left every one of them out of the list.
  async getNodeRelationships(nodeId) {
    const seen = new Set();
    const out = [];
    const ends = await Promise.all(['from', 'to'].map((end) => {
      const q = new URLSearchParams({ schema_names: 'relationship,belongs_to', limit: '500' });
      q.set(`ref[${end}]`, nodeId);
      return this.request(`/nodes?${q.toString()}`);
    }));
    for (const resp of ends) {
      for (const n of resp.nodes || []) {
        if (seen.has(n.id)) continue;
        seen.add(n.id);
        out.push(this._asRelationship(n));
      }
    }
    return this._hydrateEnds(out);
  },

  async createRelationship(fromNodeId, toNodeId, relationshipType, metadata = {}) {
    const isBelongsTo = relationshipType === 'belongs_to';
    const value_json = { from: [String(fromNodeId)], to: [String(toNodeId)] };
    // No relationship_type on belongs_to: one there would let it pose as an
    // ordinary link and slip past the schema-scoped grant that governs it.
    if (!isBelongsTo) value_json.relationship_type = relationshipType;
    if (metadata && Object.keys(metadata).length) value_json.metadata = metadata;
    const node = await this.request('/nodes', {
      method: 'POST',
      body: {
        title: relationshipType || 'relationship',
        schema_name: isBelongsTo ? 'belongs_to' : 'relationship',
        value_json,
      },
    });
    return node && node.id ? this._asRelationship(node) : node;
  },

  async deleteRelationship(relationshipId) {
    // Trash, and therefore reversible — the edge delete never was.
    return this.request(`/nodes/${relationshipId}`, { method: 'DELETE' });
  },

  // Ratification
  async ratifyNode(nodeId) {
    return this.request(`/nodes/${nodeId}/ratify`, { method: 'POST' });
  },

  async unratifyNode(nodeId) {
    return this.request(`/nodes/${nodeId}/unratify`, { method: 'POST' });
  },
};
